import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getAssetRequestOptions,
  getAssetTypeDimensions,
  getAssetTypeOptions,
  getFileFormatOptions,
  getStoreTypeOptions,
} from "../../../redux/actions/workOrderActions";
import {
  selectAssetDimensionResults,
  selectAssetRequestResults,
  selectAssetTypeResults,
  selectFileFormatResults,
  selectLanguageResults,
  selectRegionResults,
  selectStoreTypeResults,
} from "../../../redux/selectors/commonSelectors";
import {
  getLanguageOptions,
  getRegionOptions,
} from "../../../redux/actions/executionBriefListActions";
import { WORK_ORDER_TEMPLATE } from "../../../types/templates/work-order/work-order-template.enum";
import { getWorkOrderTemplateName } from "../../../types/templates/work-order/work-order-template.functions";

import { WORK_ORDER_HOOK } from "./work-order-template.interface";

export const useWorkOrderTemplateOptions = (
  hooks: WORK_ORDER_HOOK[],
  template?: WORK_ORDER_TEMPLATE
) => {
  const dispatch = useDispatch();

  const use = (hook: WORK_ORDER_HOOK) => hooks.includes(hook);

  useEffect(() => {
    if (use(WORK_ORDER_HOOK.ASSET_REQUESTS)) {
      dispatch(getAssetRequestOptions());
    }
    if (use(WORK_ORDER_HOOK.ASSET_TYPES)) {
      dispatch(getAssetTypeOptions(getWorkOrderTemplateName(template)));
    }
    if (use(WORK_ORDER_HOOK.FILE_FORMATS)) {
      dispatch(getFileFormatOptions());
    }
    if (use(WORK_ORDER_HOOK.LANGUAGES)) {
      dispatch(getLanguageOptions());
    }
    if (use(WORK_ORDER_HOOK.POSITIONS)) {
      dispatch(getAssetTypeDimensions(getWorkOrderTemplateName(template)));
    }
    if (use(WORK_ORDER_HOOK.REGIONS)) {
      dispatch(getRegionOptions());
    }
    if (use(WORK_ORDER_HOOK.STORE_TYPES)) {
      dispatch(getStoreTypeOptions(getWorkOrderTemplateName(template)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, template]);

  const assetRequestList = useSelector(selectAssetRequestResults);
  const assetRequestOptions = assetRequestList.map((request) => ({
    value: request.id,
    label: request.name,
  }));

  const assetTypeDimensionList = useSelector(selectAssetDimensionResults);
  const positionsOptions = assetTypeDimensionList.map((assetType) => {
    const name = assetType.assetTypeOption.name;
    return { value: name, label: name };
  });

  const assetTypeList = useSelector(selectAssetTypeResults);
  const assetTypeOptions = assetTypeList.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  const fileFormatList = useSelector(selectFileFormatResults);
  const fileFormatOptions = fileFormatList.map((format) => ({
    value: format.id,
    label: format.name,
  }));

  const languagesList = useSelector(selectLanguageResults);
  const languagesOptions = languagesList.map((language) => ({
    value: language.id,
    label: language.languageLong,
  }));

  const regionsList = useSelector(selectRegionResults);
  const regionsOptions = regionsList.map((region) => ({
    value: region.id,
    label: region.name,
  }));

  const storeTypeList = useSelector(selectStoreTypeResults);
  const storeTypeOptions = storeTypeList.map((format) => ({
    value: format.id,
    label: format.name,
  }));

  return {
    assetRequestList,
    assetRequestOptions,

    assetTypeDimensionList,
    positionsOptions,

    assetTypeList,
    assetTypeOptions,

    fileFormatList,
    fileFormatOptions,

    languagesList,
    languagesOptions,

    regionsList,
    regionsOptions,

    storeTypeList,
    storeTypeOptions,
  };
};
